import { useId, useMemo } from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthProvider";
import { updateUserPreferredLanguage } from "../services/userService";

const SUPPORTED_LANGUAGES = [
  { value: "en", label: "EN" },
  { value: "de", label: "DE" },
] as const;

function normalizeLanguage(language: string | undefined): "en" | "de" {
  if (!language) {
    return "en";
  }
  return language.toLowerCase().startsWith("de") ? "de" : "en";
}

type LanguageSwitcherProps = {
  className?: string;
};

export default function LanguageSwitcher({ className }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const selectId = useId();
  const currentLanguage = useMemo(
    () => normalizeLanguage(i18n.language),
    [i18n.language]
  );

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextLanguage = event.target.value as "en" | "de";
    void i18n.changeLanguage(nextLanguage);
    window.localStorage.setItem("preferredLanguage", nextLanguage);
    if (user) {
      void updateUserPreferredLanguage(user.uid, nextLanguage);
    }
  };

  return (
    <label
      htmlFor={selectId}
      className={`relative inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/20 bg-black/60 px-3 py-1 text-xs font-semibold uppercase text-white backdrop-blur ${className ?? ""}`}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="h-4 w-4 text-white/80"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M3.6 9h16.8" />
        <path d="M3.6 15h16.8" />
        <path d="M12 3a12 12 0 0 1 0 18" />
        <path d="M12 3a12 12 0 0 0 0 18" />
      </svg>
      <span>{SUPPORTED_LANGUAGES.find((language) => language.value === currentLanguage)?.label ?? "EN"}</span>
      <select
        id={selectId}
        aria-label="Language"
        value={currentLanguage}
        onChange={handleChange}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      >
        {SUPPORTED_LANGUAGES.map((language) => (
          <option key={language.value} value={language.value}>
            {language.label}
          </option>
        ))}
      </select>
    </label>
  );
}
